import { RequestOptions } from '@/types/Request'
import { ORIGIN, request } from '@/request/RequestWrapper'

const REQUEST_NAME_SPACE = 'common'

/**
 * 获取每个功能中选择数据的列表
 * @param loadingTarget 要显示 loading 的元素
 * @param type 数据列表所属的模块
 * @param requestOptions 请求选项
 */
function getDataList (loadingTarget: HTMLElement | null, type: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getSelectDataList?type=${type}`
  const options : RequestInit = {
    method: 'get'
  }
  const messages = {
    error: 'get data error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 获取每个功能中选择数据的列表 懒加载 （第三级菜单）
 * @param loadingTarget 要显示 loading 的元素
 * @param projectName 第一级菜单选定的内容
 * @param module 第二级菜单选定的内容
 * @param requestOptions 请求选项
 */
function getSelectIdList (loadingTarget: HTMLElement | null, projectName: string, module: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getSelectIdList?project_name=${projectName}&module=${module}`
  const options : RequestInit = {
    method: 'get'
  }
  const messages = {
    error: 'get data error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 获取每个功能中选择数据的列表 懒加载
 * @param loadingTarget 要显示 loading 的元素
 * @param type 数据列表所属的模块
 * @param requestOptions 请求选项
 */
function getSelectDataMenu (loadingTarget: HTMLElement | null, type: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getSelectDataMenu?type=${type}`
  const options : RequestInit = {
    method: 'get'
  }
  const messages = {
    error: 'get data error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 根据任务ID获取相应的数据名称
 * @param loadingTarget 要显示 loading 的元素
 * @param jobId 任务ID
 * @param requestOptions 请求选项
 */
function getDataName (loadingTarget: HTMLElement | null, jobId: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getDataNameById?job_id=${jobId}`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'get data error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 获取数据对应的文本内容
 * @param loadingTarget 要显示 loading 的元素
 * @param dataName 数据名称
 * @param requestOptions 请求选项
 */
function getDataContent (loadingTarget: HTMLElement | null, dataName: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getDataContent?data_name=${dataName}`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'get content error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 获得数据的json内容 筛选参数
 * @param loadingTarget 要显示 loading 的元素
 * @param chooseData 选择的数据
 * @param requestOptions 请求选项
 */
function getJson (loadingTarget: HTMLElement | null, chooseData: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getJson?choose_data=${chooseData}`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'get json error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 根据job id获取cirro地址
 * @param loadingTarget 要显示 loading 的元素
 * @param jobId 任务ID
 * @param requestOptions 请求选项
 */
function getCirroUrl (loadingTarget: HTMLElement | null, { jobId, type }: any, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getCirroUrlByJobid?job_id=${jobId}&type=${type}`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'get cirroUrl error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 更新结果数据
 * @param loadingTarget 要显示 loading 的元素
 * @param jobId 任务 ID
 * @param type 任务类型
 * @param requestOptions 请求选项
 */
function updateResult (loadingTarget: HTMLElement | null, jobId: string, type: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/updateResult?job_id=${jobId}&type=${type}`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'update result error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 获取下载文件
 * @param loadingTarget 要显示 loading 的元素
 * @param jobId 任务ID
 * @param requestOptions 请求选项
 */
function getDownloadData (loadingTarget: HTMLElement | null, jobId: string, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getDownloadUrls?job_id=${jobId}`
  const options = {
    method: 'get'
  }
  const messages = {
    success: 'get urls success',
    error: 'fail to get urls'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 上传文件
 * @param loadingTarget 要显示 loading 的元素
 * @param path { string } 上传文件的路径
 * @param file { File } 上传的文件
 * @param requestOptions 请求选项
 */
// eslint-disable-next-line camelcase
function upload (loadingTarget: HTMLElement | null, { path, file, data_name }: any, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/upload`
  const formData = new FormData()
  formData.append('file', file)
  formData.append('path', path)
  formData.append('data_name', data_name)
  const options: RequestInit = {
    body: formData,
    headers: {}
  }
  requestOptions.successMsg = requestOptions.successMsg || 'Upload success'
  requestOptions.errorMsg = requestOptions.errorMsg || 'Upload error'
  const params = { url, loadingTarget, options, ...requestOptions }
  return request(params)
}

/**
 * 获取marker上传示例文件的下载地址
 * @param loadingTarget 要显示 loading 的元素
 * @param requestOptions 请求选项
 */
function getExample (loadingTarget: HTMLElement | null, requestOptions: RequestOptions = {}) {
  const url = `${ORIGIN}/common/getExample`
  const options = {
    method: 'get'
  }
  const messages = {
    error: 'get example error'
  }
  const params = { url, loadingTarget, messages, options, ...requestOptions }
  return request(params)
}

/**
 * 检查验证码是否正确
 * @param loadingTarget 要显示 loading 的元素
 * @param captcha 验证码
 * @param requestOptions 请求选项
 */
function checkCaptcha (loadingTarget: HTMLElement | null, captcha: string, requestOptions: RequestOptions = {}) {
  const url = `${REQUEST_NAME_SPACE}/checkCaptcha`
  const options: RequestInit = {
    body: `captcha=${captcha}`
  }
  requestOptions.successMsg = requestOptions.successMsg || 'captcha right'
  requestOptions.errorMsg = requestOptions.errorMsg || 'captcha wrong'
  const params = { url, loadingTarget, options, ...requestOptions }
  return request(params)
}

export { getDataName, getSelectDataMenu, getSelectIdList, getDataContent, getJson, getDataList, getCirroUrl, getDownloadData, updateResult, checkCaptcha, upload, getExample }
